<template>
    <div>
        <div class="card">
            <div class="bg-blue-light p-1">
                <TitleButton
                    title="My Ongoing Patient"
                    :showAddNew="false"
                    :showBtn="false"
                    :showExtraBtn="true"
                    extraBtnTitle="Calendar View"
                    @onClickExtraBtn="navigateToCalendarPage"
                />
            </div>

            <div class="my-2 px-2">
                <DateQuerySetter
                    btnTitle="Go"
                    @onClickGo="getOngoingPatients"
                    :isHideDate="true"
                >
                    <div class="col-12 col-sm-6 col-md-5 col-lg-4">
                        <label for="colFormLabel" class="col-form-label">Patient</label>
                        <AsyncSelect
                            placeholder="Patient Name, ID, Mobile No"
                            v-model="patient"
                            :api-service="fetchContactProfiles"
                            :additional-query="additionalQuery"
                            :format-label="formatPatientLabel"
                        />
                    </div>
                </DateQuerySetter>
            </div>

            <div class="col-12 px-2 my-2">
                <OngoingPatientTable :patientList="patientList"/>
            </div>

            <div class="px-2 position-absolute bottom-0">
                <Pagination
                    :offset="offset"
                    @onPageChange="onPageChange"
                    ref="pagination"
                />
            </div>
        </div>

        <Loader v-if="loading"/>
    </div>
</template>

<script setup>
import TitleButton from '@/components/atom/TitleButton.vue';
import DateQuerySetter from '@/components/atom/DateQuerySetter.vue';
import OngoingPatientTable from '@/components/molecule/company/dental-os/appointment/OngoingPatientTable.vue';
import Loader from '@/components/atom/LoaderComponent.vue';
import Pagination from '@/components/atom/Pagination.vue';
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue';
import { computed, inject, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import handleDentalOs from '@/services/modules/dental-os/dentalOs';
import handleContact from '@/services/modules/contact';
import { useAsyncDropdownHelper } from '@/services/utils/asyncDropdownHelper';

const $route = useRoute();
const $router = useRouter();
const $store = useStore();

const showError = inject('showError');
const { fetchOngoingPatientList } = handleDentalOs();
const { fetchContactProfiles } = handleContact();
const { formatPatientLabel } = useAsyncDropdownHelper();

const offset = ref(10);
const loading = ref(false);
const patientList = ref([]);
const patient = ref(null);

const page = computed(() => $route.query.page);
const companyId = computed(() => $route.params.companyId);

const additionalQuery = {
    type: 'patient'
};

const setPagination = (data) => {
    $store.commit('setPaginationData', {
        records: data.total,
        perPage: data.per_page,
        currentPage: data.current_page,
    });
};

const getQuery = () => {
    let query = '?company_id=' + companyId.value;
    query += '&offset=' + offset.value;
    if (page.value) query += '&page=' + page.value;
    if (patient.value) query += '&contact_profile_id=' + patient.value.id;
    return query;
};

const getOngoingPatients = async () => {
    loading.value = true;
    try {
        const res = await fetchOngoingPatientList(getQuery());
        if (!res.status) {
            patientList.value = [];
            $store.commit('resetPagination');
            return;
        }
        patientList.value = res.data.data;
        if (patientList.value.length > 0) {
            setPagination(res.data);
        }
    } catch (err) {
        if (err.response) showError(err.response.message);
    } finally {
        loading.value = false;
    }
};

const navigateToCalendarPage = () => {
    $router.push({ name: 'my-dental-schedule', params: $route.params, query: $route.query });
};

const onPageChange = async (newPage) => {
    let routeQuery = Object.assign({}, $route.query);
    routeQuery.page = newPage;
    await $router.push({ path: $route.path, query: routeQuery });
    await getOngoingPatients();
};

onMounted(() => {
    getOngoingPatients();
});
</script>
